import { useState } from 'react';
import { Button, Snackbar, Alert, Tooltip } from '@mui/material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { userApi } from '../../services/userApi';
import { useAuthStore } from '../../stores/authStore';

interface WatchButtonProps {
  /** The ProductListing id to watch (watchlist is listing-keyed). */
  listingId: string;
  /**
   * The watchlist *item* id if this listing is already watched, else
   * null. Needed because removeWatchlist keys on the item id, not the
   * listing id.
   */
  watchedItemId: string | null;
  /**
   * Short label describing the *listing* for accessible naming, e.g.
   * "this listing at Federal" — the button toggles a watch on the specific
   * product listing, NOT the retailer, so the label must not read as the
   * vendor alone (#154).
   */
  contextLabel?: string;
  /** Invalidate the parent's watchlist query after a successful change. */
  onChanged?: () => void;
  /** Compact icon-only rendering for dense table rows. */
  iconOnly?: boolean;
}

/**
 * Per-listing "Watch" toggle (spec §7 "Save/watch button"). Logged-out
 * users are routed to /login with a return path so they land back on
 * the product page. Logged-in users toggle the listing in their
 * watchlist via POST/DELETE /user/watchlist.
 *
 * The button is controlled by `watchedItemId` supplied from the parent
 * (which owns the watchlist query), so the toggled state survives
 * re-renders and reflects pre-existing watches on first load.
 */
export function WatchButton({
  listingId,
  watchedItemId,
  contextLabel,
  onChanged,
  iconOnly = false,
}: WatchButtonProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string } | null>(
    null
  );

  const isWatched = Boolean(watchedItemId);

  const addMutation = useMutation({
    mutationFn: () => userApi.addWatchlist({ productListingId: listingId }),
    onSuccess: () => {
      setToast({ severity: 'success', message: 'Added to your watchlist.' });
      onChanged?.();
    },
    onError: (err: Error) => {
      setToast({ severity: 'error', message: `Couldn’t add to watchlist: ${err.message}` });
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => userApi.removeWatchlist(watchedItemId as string),
    onSuccess: () => {
      setToast({ severity: 'success', message: 'Removed from your watchlist.' });
      onChanged?.();
    },
    onError: (err: Error) => {
      setToast({ severity: 'error', message: `Couldn’t remove from watchlist: ${err.message}` });
    },
  });

  const pending = addMutation.isPending || removeMutation.isPending;

  function onClick() {
    if (!isAuthenticated) {
      const returnPath = `${location.pathname}${location.search}`;
      navigate(`/login?return=${encodeURIComponent(returnPath)}`);
      return;
    }
    if (isWatched) {
      removeMutation.mutate();
    } else {
      addMutation.mutate();
    }
  }

  const label = isWatched
    ? `Remove ${contextLabel ?? 'this listing'} from watchlist`
    : `Add ${contextLabel ?? 'this listing'} to watchlist`;
  const text = isWatched ? 'Watching' : 'Watch';
  const icon = isWatched ? (
    <BookmarkIcon fontSize="small" />
  ) : (
    <BookmarkBorderIcon fontSize="small" />
  );

  return (
    <>
      <Tooltip title={isAuthenticated ? label : 'Sign in to watch this listing'}>
        <span>
          <Button
            size="small"
            variant={isWatched ? 'contained' : 'outlined'}
            color={isWatched ? 'primary' : 'inherit'}
            onClick={onClick}
            disabled={pending}
            aria-label={label}
            aria-pressed={isWatched}
            startIcon={iconOnly ? undefined : icon}
            sx={{ whiteSpace: 'nowrap', minWidth: iconOnly ? 0 : undefined }}
          >
            {iconOnly ? icon : text}
          </Button>
        </span>
      </Tooltip>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}

export default WatchButton;
